import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import {
  Observable,
  catchError,
  combineLatest,
  map,
  of,
  shareReplay,
  skipWhile,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { pagesPATH } from 'src/app/models_new/config/pages';
import { SimulationApiService } from 'src/app/services/api/simulation-api.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportWidgetService } from 'src/app/services/support-widget.service';
import { DataRequestState } from 'src/app/data-request/model';
import { MatStepper } from '@angular/material/stepper';
import { toRequestState } from 'src/app/data-request/operators';
import { IOrganizationContextResolverData } from 'src/app/resolvers/organization-context-resolver.resolver';
import { IApiScene } from 'src/app/models_new/classes/api-models/ApiScene';
import { ApiPattern } from 'src/app/models_new/classes/api-models/ApiPattern';
import { IApiStrategy } from 'src/app/models_new/classes/api-models/ApiStrategy';
import { SimulationStatus } from 'src/app/models_new/enums/simulation-status';
import {
  ISceneApiFileType,
  IStrategyApiFileType,
} from 'src/app/models_new/types/simulation-api-file-type';
import { PalletComponent } from '../../patterns/pattern/pallet/pallet.component';
import { Project } from 'src/app/models_new/classes/project';
import { ExportImportService } from 'src/app/services/export-import.service';
import { defaultApiPattern } from 'src/app/models_new/config/default/api-default/default-api-pattern';
import { ObjectUtils } from 'src/app/utils/object';
import { HardwareConfigurationCardComponent } from '../../gui/hardware-configuration-card/hardware-configuration-card.component';
import { toScene } from 'src/app/services/robot-config-helper';
import { SoftwareDetailsComponent } from '../../inventory/software/software-details/software-details.component';
import { IPallyFileType } from 'src/app/models_new/types/pally-file-type';
import { isEqual } from 'lodash-es';
import { LocalStorageKey } from 'src/app/models_new/enums/local-storage-keys';
import { LocalStorageService } from 'src/app/services/local-storage.service';

export interface ISimulationRetry {
  id: string;
  name: string;
  edited_scene: ISceneApiFileType;
  edited_patterns: {
    left?: IPallyFileType;
    right?: IPallyFileType;
  };
  edited_strategy: IStrategyApiFileType;
  error_details: string;
  simulation_state: SimulationStatus;
  simulation_status: any;
  pattern: { id: string; data: IPallyFileType };
  second_pattern: { id: string; data: IPallyFileType };
  scene: { id: string; data: ISceneApiFileType };
  strategy: { id: string; data: IStrategyApiFileType };
}

interface IRetryData {
  original: IPallyFileType | ISceneApiFileType | IStrategyApiFileType | any;
  edited?: IPallyFileType | ISceneApiFileType | IStrategyApiFileType | any;
  altInitFormat?: any;
}

@Component({
  selector: 'app-simulation-retry',
  templateUrl: './simulation-retry.component.html',
  styleUrls: ['./simulation-retry.component.scss'],
})
export class SimulationRetryComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('palletComponentPrimary') palletComponentPrimary: PalletComponent;
  @ViewChild('palletComponentSecondary')
  palletComponentSecondary: PalletComponent;
  @ViewChild('hardwareConfigurationComponent')
  hardwareConfigurationComponent: HardwareConfigurationCardComponent;
  @ViewChild('simulationConfigurationComponent')
  simulationConfigurationComponent: SoftwareDetailsComponent;

  suggestionCard$: Observable<{
    error: string;
    info?: string;
    suggestions: string[];
  }>;

  private organizationId: string;
  init$: Observable<DataRequestState<boolean>>;

  // Orignal & Edited params:
  private originalSimulation: ISimulationRetry;
  pattern: IRetryData;
  secondPattern: IRetryData;
  hardware: IRetryData;
  software: IRetryData;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private simulationService: SimulationApiService,
    private notificationService: NotificationService,
    private supportWidgetService: SupportWidgetService,
    private exportImportService: ExportImportService,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.route.data
      .pipe(
        take(1),
        map(
          (data: Data) =>
            (data as IOrganizationContextResolverData).organization_id
        )
      )
      .subscribe((orgId) => (this.organizationId = orgId));

    this.suggestionCard$ = this.simulationService
      .getSimulationRetryData(this.route.snapshot.params.id)
      .pipe(
        take(1),
        tap((simulation: ISimulationRetry) => {
          // Initialize original & edited params.
          // Pattern:
          const pattern =
            simulation.edited_patterns.right || simulation.pattern.data;

          this.pattern = {
            original: ObjectUtils.cloneObject(pattern),
            edited: ObjectUtils.cloneObject(pattern),
            altInitFormat: this.exportImportService.mapToProjectFormat(
              pattern,
              true,
              true,
              pattern.modelType
            ),
          };

          const secondPattern =
            simulation.edited_patterns.left || simulation.second_pattern?.data;

          if (secondPattern) {
            this.secondPattern = {
              original: ObjectUtils.cloneObject(secondPattern),
              edited: ObjectUtils.cloneObject(secondPattern),
              altInitFormat: this.exportImportService.mapToProjectFormat(
                secondPattern,
                true,
                true,
                secondPattern.modelType
              ),
            };
          }

          // Scene:
          const scene = simulation.edited_scene || simulation.scene.data;
          this.hardware = {
            original: ObjectUtils.cloneObject(scene),
            edited: ObjectUtils.cloneObject(scene),
          };

          // Strategy:
          const strategy =
            simulation.edited_strategy || simulation.strategy.data;
          this.software = {
            original: ObjectUtils.cloneObject(strategy),
            edited: ObjectUtils.cloneObject(strategy),
            altInitFormat: {
              ...simulation.strategy,
              name: this.getSimulationRetryName(simulation.name),
              data: strategy,
            },
          };
          this.originalSimulation = simulation;
        }),
        map((simulation: ISimulationRetry) => {
          if (
            simulation?.error_details &&
            simulation?.simulation_state === SimulationStatus.ERROR
          ) {
            const errInfo =
              simulation.simulation_status?.status_message?.error_info;
            return {
              error: this.getFormattedError(simulation.error_details, errInfo),
              extended_error:
                simulation.simulation_status?.ext_error_msg || null,
              suggestions: simulation.simulation_status?.status_message
                ?.suggested_action
                ? simulation.simulation_status.status_message.suggested_action.split(
                    '.'
                  )
                : null,
            };
          } else {
            return {
              info: 'Here can you tweak some details of the original simulation to seek for the best results',
              error: null,
              suggestions: [
                'Edit the settings you would like to change for the next simulation',
              ],
            };
          }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError((e) => {
          console.error(e);
          this.notificationService.showError(
            `Can't fetch simulation with ID: ${this.route.snapshot.params.id}`
          );
          this.router.navigate([pagesPATH.SIMULATIONS]);
          return [null];
        })
      );
    this.supportWidgetService.elevateSupportButton();
    this.init$ = of(true).pipe(toRequestState());
  }

  ngOnDestroy(): void {
    this.supportWidgetService.resetSupportButtonStyling();
  }

  public backClick(_stepper: MatStepper): void {
    if (_stepper.selectedIndex === 0) {
      this.router.navigate([pagesPATH.SIMULATIONS]);
      return;
    }
    this.stepper.previous();
  }

  public nextClick(stepper: MatStepper): void {
    const saveOnIndex = this.secondPattern ? 3 : 2;
    if (stepper.selectedIndex === saveOnIndex) {
      this.confirmClickAndSaveSim();
      return;
    }

    if (stepper.selectedIndex === 0) {
      this.palletComponentPrimary.asyncProject$
        .pipe(
          skipWhile((drs: DataRequestState<Project>) => !drs.value),
          map((drs: DataRequestState<Project>) => drs.value)
        )
        .pipe(
          take(1),
          switchMap((project) => {
            const pattern = new ApiPattern(
              ObjectUtils.cloneObject(defaultApiPattern)
            );
            const ex = this.exportImportService.mapToExportFormat(project);
            this.localStorage.removeData(LocalStorageKey.PROJECT);
            pattern.data = ex;
            pattern.name = project.data.name;
            pattern.data.name = project.data.name;
            return of(pattern);
          })
        )
        .subscribe({
          next: (newPattern) => {
            this.notificationService.showSuccess('Primary pattern updated');
            this.pattern.edited = newPattern;
          },
          error: (err) => {
            this.notificationService.showError('Error storing primary pattern');
            console.error(err);
            this.localStorage.removeData(LocalStorageKey.PROJECT);
          },
        });
    }

    if (stepper.selectedIndex === 1 && this.secondPattern) {
      this.palletComponentSecondary.asyncProject$
        .pipe(
          skipWhile((drs: DataRequestState<Project>) => !drs.value),
          map((drs: DataRequestState<Project>) => drs.value)
        )
        .pipe(
          take(1),
          switchMap((project) => {
            const pattern = new ApiPattern(
              ObjectUtils.cloneObject(defaultApiPattern)
            );
            const ex = this.exportImportService.mapToExportFormat(project);
            this.localStorage.removeData(LocalStorageKey.PROJECT);
            pattern.data = ex;
            pattern.name = project.data.name;
            pattern.data.name = project.data.name;
            return of(pattern);
          })
        )
        .subscribe({
          next: (newPattern) => {
            this.notificationService.showSuccess('Secondary pattern updated');
            this.secondPattern.edited = newPattern;
          },
          error: (err) => {
            this.notificationService.showError(
              'Error storing secondary pattern'
            );
            console.error(err);
            this.localStorage.removeData(LocalStorageKey.PROJECT);
          },
        });
    }

    const renderSceneOnIndex = this.secondPattern ? 2 : 1;
    if (stepper.selectedIndex === renderSceneOnIndex) {
      this.hardware.edited = toScene(
        this.hardwareConfigurationComponent.simConfigService.getAllFields(
          this.hardwareConfigurationComponent.threeID
        )
      );
      this.notificationService.showSuccess('Hardware updated.');
    }

    this.stepper.next();
  }

  /**
   * Inserts a new simulation using the original simulation's data, inserting the edited fields apart,
   * and relating the original Simulation under origin_simulation_id.
   */
  public confirmClickAndSaveSim(): void {
    // Update software as part of last step
    this.software.edited =
      this.simulationConfigurationComponent.getStrategyOnDemand();

    let leftPatternSource$: Observable<IPallyFileType>;
    let rightPatternSource$: Observable<IPallyFileType>;
    let hardwareSource$: Observable<IApiScene['data']>;
    let softwareSource$: Observable<IApiStrategy['data']>;

    // Method for sorting objects so its an exact match
    const sortObject = (obj) => {
      if (typeof obj !== 'object' || obj === null) return obj;
      if (Array.isArray(obj)) return obj.map(sortObject);
      return Object.keys(obj)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = sortObject(obj[key]);
          return sorted;
        }, {});
    };

    // Check equality of original and edited patterns
    if (
      !isEqual(
        sortObject({ ...this.pattern.original, id: undefined }),
        sortObject({ ...this.pattern.edited, id: undefined })
      )
    ) {
      // Update pattern name after equal check
      this.pattern.edited.name = this.getSimulationRetryName(
        this.pattern.original.name
      );
      this.pattern.edited.data.name = this.pattern.edited.name;
      // Add pattern insert to sources
      rightPatternSource$ = of(this.pattern.edited.data);
    }

    if (
      this.secondPattern &&
      !isEqual(
        sortObject({ ...this.secondPattern.original, id: undefined }),
        sortObject({ ...this.secondPattern.edited.data, id: undefined })
      )
    ) {
      // Update pattern name after equal check
      this.secondPattern.edited.name = this.getSimulationRetryName(
        this.secondPattern.original.name
      );
      this.secondPattern.edited.data.name = this.secondPattern.edited.name;
      // Add pattern insert to sources
      leftPatternSource$ = of(this.secondPattern.edited.data);
    }

    // Check equality of original and edited hardware
    if (
      !isEqual(
        sortObject({ ...this.hardware.original, id: undefined }),
        sortObject({ ...this.hardware.edited.data, id: undefined })
      )
    ) {
      // Add hardware insert to sources
      hardwareSource$ = of(this.hardware.edited);
    }

    // Check equality of original and edited software
    if (
      !isEqual(
        sortObject(this.software.original),
        sortObject(this.software.edited)
      )
    ) {
      // Add software insert to sources
      softwareSource$ = of(this.software.edited.strategy);
    }

    combineLatest([
      leftPatternSource$ || of(null),
      rightPatternSource$ || of(null),
      hardwareSource$ || of(null),
      softwareSource$ || of(null),
    ])
      .pipe(
        take(1),
        switchMap(([leftPattern, rightPattern, hardware, software]) =>
          this.simulationService.insertSimulationRetry(
            {
              name: this.getSimulationRetryName(this.originalSimulation.name),
              simulation_state: SimulationStatus.QUEUED,
              pattern_id: this.originalSimulation.pattern.id,
              second_pattern_id: this.originalSimulation.second_pattern?.id,
              scene_id: this.originalSimulation.scene.id,
              strategy_id: this.originalSimulation.strategy.id,
              organization_id: this.organizationId,
              id: this.originalSimulation.id,
            },
            {
              left: leftPattern,
              right: rightPattern,
            },
            hardware,
            software
          )
        )
      )
      .subscribe({
        next: (_res) => {
          this.notificationService.showSuccess('Software updated.');
          this.notificationService.showSuccess('New Simulation queued.');
          this.router.navigate([pagesPATH.SIMULATIONS]);
        },
        error: (err) => {
          this.notificationService.showError('Error creating simulation');
          console.error(err);
        },
      });
  }

  /**
   * Returns consistent SimulatonRetry names and adds numeration if > 1.
   * @param {string} name Original name of the item
   * @returns {string} Original name added ' - Retry' or incremented numeration
   */
  private getSimulationRetryName(name: string): string {
    const separator = [' - Retry', ' - Retry '];
    if (name.includes(separator[1])) {
      const nameSplitted = name.split(separator[1]);
      return nameSplitted[0] + separator[1] + (Number(nameSplitted[1]) + 1);
    } else if (name.includes(separator[0])) {
      return name + ' 2';
    } else {
      return name + separator[0];
    }
  }

  private getFormattedError(errorDetails: string, errorInfo: any): string {
    let error = errorDetails;
    if (errorInfo) {
      for (let [key, value] of Object.entries(errorInfo)) {
        if (key === 'box_id') {
          key = 'Box Number';
          value = +value + 1;
        }
        error += `\n- ${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`;
      }
    }
    return error;
  }
}
